import React from 'react';
import { Link } from 'react-router-dom';
import { Receipt, FileText, Building2, Landmark, CreditCard, FileCheck, ArrowRight } from 'lucide-react';

const PATTERNS = [
  {
    icon: Receipt,
    title: 'Receipts and expense docs',
    body:
      'Ride-share PDFs, Amazon order confirmations, SaaS invoices, emailed e-receipts. Sortio reads each one for vendor, date, and amount, renames to 2026-04-12_Vendor_$142.83.pdf, and files under the right client and expense category. See our full pattern for naming PDFs by content.',
    link: '/blog/automatically-rename-pdfs-by-content',
    linkLabel: 'Read the PDF rename guide',
  },
  {
    icon: FileText,
    title: 'Invoices in and out',
    body:
      'Vendor bills your client receives, and invoices your client sends to their own customers. Sortio recognizes both directions and files them under accounts payable or accounts receivable inside the client folder, tagged by month so reconciliation is one Finder filter away.',
  },
  {
    icon: Landmark,
    title: 'Bank and brokerage statements',
    body:
      'Monthly statements arrive as Chase_Statement_Apr2026.pdf or Fidelity_1099-DIV.pdf or, worse, just statement.pdf. Sortio extracts the institution, account number tail, and statement period, then files by client, account, and year. Year-end downloads from a brokerage land in the right tax year folder automatically.',
  },
  {
    icon: FileCheck,
    title: '1099s, W-2s, K-1s',
    body:
      'Sortio recognizes the form type (1099-NEC, 1099-MISC, 1099-INT, 1099-DIV, 1099-B, W-2, K-1, 1095-A) and the issuing entity. A folder of 30 "1099.pdf" files becomes 30 cleanly named forms organized by client, then by form type. The 1099-NEC pile from one client gets sub-grouped by payer.',
  },
  {
    icon: Building2,
    title: 'Entity-level vs personal returns',
    body:
      'Many of your clients have two parallel returns: an S-corp or partnership return, and their personal 1040. Sortio keeps the entity folder and the personal folder separate by entity ID and Social, so a vehicle expense receipt does not accidentally land in the personal return folder.',
  },
  {
    icon: CreditCard,
    title: 'Payroll and quarterly filings',
    body:
      'For your bookkeeping clients running payroll, Sortio routes 941 and state quarterly filings into a payroll subfolder by quarter, and pulls year-end W-3 transmittals into the year-end binder folder. Payroll provider exports (Gusto, ADP, Paychex) are detected by their PDF templates.',
  },
];

const FilingPatterns = () => {
  return (
    <section id="patterns" className="my-20">
      <div className="text-center mb-14">
        <h2 className="text-3xl sm:text-4xl font-semibold mb-4 tracking-tight text-apple-50">
          The filing patterns CPAs actually need
        </h2>
        <p className="text-apple-300 text-lg font-light max-w-2xl mx-auto">
          Sortio recognizes the document categories that show up inside a tax or bookkeeping
          engagement and routes each one.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {PATTERNS.map((p, i) => {
          const Icon = p.icon;
          return (
            <div key={i} className="bg-apple-800/40 backdrop-blur-sm rounded-2xl border border-apple-700/50 p-6">
              <div className="w-10 h-10 rounded-full bg-sortio-primary/15 border border-sortio-primary/30 flex items-center justify-center mb-4">
                <Icon className="text-sortio-primary" size={18} />
              </div>
              <h3 className="text-lg font-medium text-apple-50 mb-3">{p.title}</h3>
              <p className="text-sm text-apple-200 leading-relaxed">{p.body}</p>
              {p.link && (
                <Link to={p.link} className="inline-flex items-center mt-3 text-sortio-primary hover:text-sortio-secondary transition-colors text-xs font-medium">
                  {p.linkLabel} <ArrowRight className="ml-1" size={12} />
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default FilingPatterns;
